import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Plus, Minus, Loader2, Search, X, ChevronDown } from "lucide-react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const PER_POINT_OMR = 5;
const MAX_POINTS = 4;

/** Logo URLs for each whitelisted company — fed into <img> tags on the card. */
const COMPANY_LOGO = {
    "الحميري للذهب": "https://customer-assets.emergentagent.com/job_accounting-pro-76/artifacts/si18grip_IMG_1542.jpeg",
    "الراقيه للذهب": "https://customer-assets.emergentagent.com/job_accounting-pro-76/artifacts/vijo3rsj_IMG_1541.jpeg",
    "الراقية للذهب": "https://customer-assets.emergentagent.com/job_accounting-pro-76/artifacts/vijo3rsj_IMG_1541.jpeg",
};

function logoFor(company) {
    const n = (company?.company_name || "").trim();
    return COMPANY_LOGO[n] || null;
}

/* ──────────────────────────────────────────────────────────────────────── */
/*                              LIST PAGE                                   */
/* ──────────────────────────────────────────────────────────────────────── */
export default function DiscountCustomers() {
    const navigate = useNavigate();
    const { company } = useAuth();
    const { lang } = useLang();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const logo = logoFor(company);

    const load = async ({ silent = false } = {}) => {
        try {
            const r = await api.get("/discount-customers");
            setItems(r.data || []);
        } catch (e) {
            if (!silent) toast.error(formatApiError(e));
        } finally {
            if (!silent) setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Silent auto-refresh — new public-registered customers + point changes
    // from other devices appear within 10 seconds without a page reload.
    const silentRefresh = useCallback(() => { load({ silent: true }); }, []);
    useAutoRefresh(silentRefresh, { intervalMs: 10000 });

    // Search by phone, membership_id, or name. Strip whitespace + leading '#'
    // from the query so users can paste either "#000003" or "000003".
    const filtered = useMemo(() => {
        const q = query.trim().replace(/^#/, "").toLowerCase();
        if (!q) return items;
        return items.filter((c) => {
            const phone = (c.phone || "").toLowerCase();
            const mid = (c.membership_id || "").toLowerCase();
            const name = (c.name || "").toLowerCase();
            return phone.includes(q) || mid.includes(q) || name.includes(q);
        });
    }, [items, query]);

    return (
        <AppShell>
            <div className="mx-auto max-w-5xl">
                <div className="mb-6">
                    <button onClick={() => navigate("/history")} className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900" data-testid="dc-back-btn">
                        <ArrowRight className="h-4 w-4" />
                        {lang === "ar" ? "العودة للسجلات" : "Back to records"}
                    </button>
                    <h1 className="mt-1 font-heading text-2xl font-bold sm:text-3xl">
                        {lang === "ar" ? "سجل خصم الزباين" : "Customer Discounts"}
                    </h1>
                    <p className="text-xs text-neutral-500">
                        {lang === "ar"
                            ? `بطاقات ولاء — كل نقطة تساوي ${PER_POINT_OMR} ر.ع · حد أقصى ${MAX_POINTS} نقاط = ${PER_POINT_OMR * MAX_POINTS} ر.ع خصم`
                            : `Loyalty cards — each point = ${PER_POINT_OMR} OMR · max ${MAX_POINTS} points`}
                    </p>
                </div>

                {/* ── Search bar ── */}
                <div className="relative mb-5">
                    <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={lang === "ar" ? "ابحث برقم الهاتف أو رقم العضوية أو الاسم..." : "Search by phone, membership ID or name..."}
                        className="h-11 pe-10 ps-10 text-sm"
                        data-testid="dc-search-input"
                    />
                    {query ? (
                        <button
                            type="button"
                            onClick={() => setQuery("")}
                            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                            data-testid="dc-search-clear"
                            aria-label="Clear search"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    ) : null}
                </div>

                {loading ? (
                    <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/50 p-10 text-center text-neutral-500">
                        <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/50 p-10 text-center text-neutral-500" data-testid="dc-empty">
                        {lang === "ar"
                            ? "لا يوجد زبائن بعد — الزبائن يسجلون عبر رابط البطاقة العام"
                            : "No customers yet — they register via the public link"}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/50 p-10 text-center text-neutral-500" data-testid="dc-no-results">
                        {lang === "ar"
                            ? `لا توجد نتائج لـ "${query}"`
                            : `No results for "${query}"`}
                    </div>
                ) : (
                    <>
                        <div className="mb-3 text-xs text-neutral-500" data-testid="dc-result-count">
                            {lang === "ar"
                                ? `${filtered.length} زبون${filtered.length === items.length ? "" : ` من أصل ${items.length}`}`
                                : `${filtered.length} customer${filtered.length === 1 ? "" : "s"}${filtered.length === items.length ? "" : ` of ${items.length}`}`}
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {filtered.map((c) => (
                                <Link key={c.id} to={`/discount-customers/${c.id}`} data-testid={`dc-card-${c.id}`} className="block">
                                    <CustomerMiniCard customer={c} logo={logo} />
                                </Link>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </AppShell>
    );
}

function CustomerMiniCard({ customer, logo }) {
    return (
        <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-heading text-base font-bold leading-tight">{customer.name}</div>
                    <div className="font-mono text-xs text-neutral-500">#{customer.membership_id} · {customer.phone}</div>
                </div>
                {logo ? <img src={logo} alt="logo" className="h-10 w-10 rounded-full object-cover" /> : null}
            </div>
            <div className="mt-3 flex items-center gap-1.5">
                {[...Array(MAX_POINTS)].map((_, i) => {
                    const filled = i < (customer.points || 0);
                    return (
                        <div
                            key={i}
                            className={`flex h-7 w-7 items-center justify-center rounded-full ring-2 ${
                                filled ? "bg-amber-700 ring-amber-700" : "bg-white ring-neutral-200"
                            }`}
                        >
                            {filled && logo ? (
                                <img src={logo} alt="" className="h-5 w-5 rounded-full object-cover" />
                            ) : null}
                        </div>
                    );
                })}
                <div className="ml-auto text-xs font-bold text-amber-800">
                    {(customer.points || 0) * PER_POINT_OMR} ر.ع
                </div>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────── */
/*                        SINGLE CUSTOMER (CARD VIEW)                       */
/* ──────────────────────────────────────────────────────────────────────── */
export function DiscountCustomerDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { company } = useAuth();
    const { lang } = useLang();
    const [customer, setCustomer] = useState(null);
    const [busy, setBusy] = useState(false);
    const logo = logoFor(company);

    const load = async ({ silent = false } = {}) => {
        try {
            const r = await api.get(`/discount-customers/${id}`);
            setCustomer(r.data);
        } catch (e) {
            if (!silent) toast.error(formatApiError(e));
        }
    };
    useEffect(() => { load(); }, [id]);

    // Live sync — picks up point changes triggered from other devices.
    const silentRefresh = useCallback(() => {
        if (!busy) load({ silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, busy]);
    useAutoRefresh(silentRefresh, { intervalMs: 10000 });

    const change = async (action) => {
        setBusy(true);
        try {
            const r = await api.post(`/discount-customers/${id}/${action}`);
            setCustomer(r.data);
            toast.success(
                action === "add-point"
                    ? (lang === "ar" ? "تمت إضافة نقطة" : "Point added")
                    : (lang === "ar" ? "تم استخدام نقطة" : "Point used")
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    if (!customer) {
        return (
            <AppShell>
                <div className="flex items-center justify-center p-10">
                    <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
                </div>
            </AppShell>
        );
    }

    const points = customer.points || 0;
    const vipPoints = customer.vip_points || 0;
    const completedCards = customer.completed_cards || 0;
    const VIP_MAX = 3;
    const VIP_PER = 20;
    const totalDiscount = points * PER_POINT_OMR;
    const vipTotal = vipPoints * VIP_PER;
    // "متبقي على البطاقة الذهبية" — VIP stamps still needed (max 3).
    const vipRemaining = VIP_MAX - vipPoints;
    const progressMessage = completedCards === 0
        ? null
        : completedCards === 1
        ? (lang === "ar" ? "تم استلام كرت كامل — متبقي كرتان للحصول على ختم ذهبي" : "1 card done — 2 more for a gold stamp")
        : completedCards === 2
        ? (lang === "ar" ? "تم استلام كرتين كاملين — متبقي كرت واحد للحصول على ختم ذهبي" : "2 cards done — 1 more for a gold stamp")
        : null;
    const vipOptions = Array.from({ length: vipPoints }, (_, i) => (i + 1) * VIP_PER);

    const onVipRedeem = async (amount) => {
        setBusy(true);
        try {
            const r = await api.post(`/discount-customers/${id}/vip-redeem`, { amount });
            setCustomer(r.data);
            toast.success(lang === "ar" ? `تم استخدام ${amount} ر.ع` : `Redeemed ${amount} OMR`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AppShell>
            <div className="mx-auto max-w-md">
                <button onClick={() => navigate("/discount-customers")} className="mb-3 inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900" data-testid="dcd-back-btn">
                    <ArrowRight className="h-4 w-4" />
                    {lang === "ar" ? "العودة للقائمة" : "Back to list"}
                </button>

                {/* ── REGULAR CARD ── */}
                <div className="rounded-3xl border border-neutral-900/10 bg-white p-6 shadow-xl" data-testid="dcd-card" dir="rtl">
                    <div className="mb-5 flex items-center gap-3 border-b border-neutral-200 pb-4">
                        {logo ? <img src={logo} alt="brand" className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-700/30" /> : null}
                        <div className="flex-1 text-right">
                            <div className="font-heading text-lg font-extrabold leading-tight">{company?.company_name}</div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">LOYALTY CARD · بطاقة ولاء</div>
                        </div>
                    </div>

                    <div className="mb-5 grid grid-cols-4 gap-3">
                        {[...Array(MAX_POINTS)].map((_, i) => {
                            const filled = i < points;
                            return (
                                <div
                                    key={i}
                                    className={`relative aspect-square rounded-full ring-4 ${filled ? "bg-white ring-amber-700" : "bg-neutral-100 ring-neutral-200"} flex items-center justify-center overflow-hidden`}
                                    data-testid={`dcd-stamp-${i}`}
                                >
                                    {logo ? (
                                        <img src={logo} alt="" className={`h-full w-full object-cover transition-opacity ${filled ? "opacity-100" : "opacity-15"}`} />
                                    ) : (
                                        <span className={`text-2xl font-bold ${filled ? "text-amber-700" : "text-neutral-300"}`}>★</span>
                                    )}
                                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rounded-full bg-amber-700 px-1.5 py-0.5 text-[9px] font-bold text-white shadow">
                                        {PER_POINT_OMR} ر.ع
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mb-4 grid grid-cols-2 gap-3 border-t border-neutral-200 pt-4">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{lang === "ar" ? "الاسم" : "Name"}</div>
                            <div className="font-heading text-base font-bold">{customer.name}</div>
                            <div className="font-mono text-xs text-neutral-500">{customer.phone}</div>
                        </div>
                        <div className="text-left">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{lang === "ar" ? "متبقي على البطاقة الذهبية" : "Remaining to Gold"}</div>
                            <div className="font-heading text-3xl font-extrabold text-amber-700" data-testid="dcd-remaining">{vipRemaining}</div>
                        </div>
                    </div>

                    <div className="rounded-xl bg-amber-50 p-4 text-center ring-1 ring-amber-200" data-testid="dcd-total">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">{lang === "ar" ? "إجمالي مبلغ الخصم" : "Total discount"}</div>
                        <div className="mt-1 font-heading text-3xl font-extrabold text-amber-900">{totalDiscount}<span className="mr-1 text-base"> ر.ع</span></div>
                    </div>

                    <div className="mt-4 text-center font-mono text-sm font-bold tracking-widest text-neutral-700" data-testid="dcd-membership">
                        #{customer.membership_id}
                    </div>
                </div>

                {progressMessage ? (
                    <div className="mt-3 rounded-xl border border-amber-300/60 bg-gradient-to-r from-amber-50 to-yellow-50 px-4 py-3 text-center text-sm font-bold text-amber-900" data-testid="dcd-progress-msg">
                        {progressMessage}
                    </div>
                ) : null}

                {/* Regular card action buttons */}
                <div className="mt-5 flex gap-3">
                    <Button
                        onClick={() => change("add-point")}
                        disabled={busy}
                        className="h-12 flex-1 gap-2 bg-emerald-700 text-base font-bold text-white hover:bg-emerald-800 disabled:opacity-50"
                        data-testid="dcd-add-btn"
                    >
                        <Plus className="h-5 w-5" />
                        {lang === "ar" ? "إضافة نقطة" : "Add point"}
                    </Button>
                    <Button
                        onClick={() => change("use-point")}
                        disabled={busy || points <= 0}
                        className="h-12 flex-1 gap-2 bg-rose-700 text-base font-bold text-white hover:bg-rose-800 disabled:opacity-50"
                        data-testid="dcd-use-btn"
                    >
                        <Minus className="h-5 w-5" />
                        {lang === "ar" ? "استخدام نقطة" : "Use point"}
                    </Button>
                </div>

                {/* ── GOLD VIP CARD ── */}
                <div
                    className="mt-6 overflow-hidden rounded-3xl p-6 shadow-xl ring-1 ring-amber-500/40"
                    style={{ background: "linear-gradient(140deg, #d4af37 0%, #a67c11 45%, #4a3608 100%)" }}
                    dir="rtl"
                    data-testid="dcd-vip-card"
                >
                    <div className="mb-4 flex items-center gap-3 border-b border-white/20 pb-3">
                        {logo ? <img src={logo} alt="brand" className="h-11 w-11 rounded-full object-cover ring-2 ring-white/50" /> : null}
                        <div className="flex-1 text-right">
                            <div className="flex items-center justify-end gap-2">
                                <span className="rounded-md bg-black/40 px-2 py-0.5 text-[10px] font-black tracking-widest text-amber-100">VIP</span>
                                <div className="font-heading text-lg font-extrabold leading-tight text-white">{company?.company_name}</div>
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-100/90">GOLD CARD · البطاقة الذهبية</div>
                        </div>
                    </div>

                    <div className="mb-4 grid grid-cols-3 gap-3">
                        {[...Array(VIP_MAX)].map((_, i) => {
                            const filled = i < vipPoints;
                            return (
                                <div
                                    key={i}
                                    className={`relative aspect-square rounded-full ring-4 ${filled ? "bg-white ring-amber-100" : "bg-black/25 ring-white/30"} flex items-center justify-center overflow-hidden`}
                                    data-testid={`dcd-vip-stamp-${i}`}
                                >
                                    {logo ? (
                                        <img src={logo} alt="" className={`h-full w-full object-cover transition-opacity ${filled ? "opacity-100" : "opacity-15"}`} />
                                    ) : (
                                        <span className={`text-2xl font-bold ${filled ? "text-amber-900" : "text-white/40"}`}>★</span>
                                    )}
                                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rounded-full bg-neutral-900 px-1.5 py-0.5 text-[9px] font-bold text-amber-200 shadow">
                                        {VIP_PER} ر.ع
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="rounded-xl bg-black/25 p-4 text-center ring-1 ring-white/15" data-testid="dcd-vip-total">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-100/80">{lang === "ar" ? "إجمالي خصم البطاقة الذهبية" : "Gold total"}</div>
                        <div className="mt-1 font-heading text-3xl font-extrabold text-white">{vipTotal}<span className="mr-1 text-base text-amber-100/90"> ر.ع</span></div>
                    </div>
                </div>

                {/* VIP redeem menu */}
                <div className="mt-4">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                disabled={busy || vipPoints <= 0}
                                className="h-12 w-full gap-2 bg-neutral-900 text-base font-bold text-amber-100 hover:bg-neutral-800 disabled:opacity-50"
                                data-testid="dcd-vip-redeem-trigger"
                            >
                                {lang === "ar" ? "استخدام خصم البطاقة الذهبية" : "Redeem Gold discount"}
                                <ChevronDown className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56" align="center">
                            {vipOptions.length === 0 ? (
                                <DropdownMenuItem disabled>{lang === "ar" ? "لا أختام متاحة" : "No stamps"}</DropdownMenuItem>
                            ) : (
                                vipOptions.map((amt) => (
                                    <DropdownMenuItem
                                        key={amt}
                                        onClick={() => onVipRedeem(amt)}
                                        className="cursor-pointer justify-center font-bold"
                                        data-testid={`dcd-vip-redeem-${amt}`}
                                    >
                                        {lang === "ar" ? `استخدام ${amt} ر.ع` : `Redeem ${amt} OMR`}
                                    </DropdownMenuItem>
                                ))
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </AppShell>
    );
}
